import React, {useCallback, useEffect, useState} from 'react';
import {useHeader} from "../../contexts/HeaderContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import {IconFilter, IconSpinner} from "../../../components/icons";
import {getDashoardFilter, getStatisticIndicateur} from "../../../components/requests";
import {Partenaire, Source, Statut} from "../../../components/models";
import {useFormik} from "formik";
import BarChart from "./components/BarChart";
import LineChart from "./components/LineChart";
import {prepareMonth, prepareData, calculateMoyenne, sumArrayData, roundNumber} from "../../../components/helpers";
import {useAuth} from "../../contexts/AuthContext";
import MultipleBarChart from "./components/MultipleBarChart";
import Select from "react-select";
import {mois} from "../../../components/data";

// type du selecteur multiple des partenaires
type Option = {
  value: string;
  label: string;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
);

const IndicateurDashboardPage = () => {
  const {setTitle} = useHeader();
  const {currentUser, isAdmin, isGestionnaire} = useAuth();
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [isFiltreLoading, setIsFiltreLoading] = useState<boolean>(true);
  const [partenaires, setPartenaires] = useState<Partenaire[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [statuts, setStatuts] = useState<Statut[]>([]);
  const [sourceQuery, setSourceQuery] = useState<string>('');
  const [anneeQuery, setAnneeQuery] = useState<string>('');
  const [moisQuery, setMoisQuery] = useState<string>('');
  const [statutQuery, setStatutQuery] = useState<string>('');
  const [partenaireQuery, setPartenaireQuery] = useState<string>('');
  const [monthData, setMonthData] = useState<any[]>([]);
  const [vehiculeEnleveTotal, setVehiculeEnleveTotal] = useState<number[]>([]);
  const [vehiculeEnleveTotalLastYear, setVehiculeEnleveTotalLastYear] = useState<number[]>([]);
  const [remorqueTTCTotal, setRemorqueTTCTotal] = useState<number[]>([]);
  const [gardiennageTTCTotal, setGardiennageTTCTotal] = useState<number[]>([]);
  const [autreFraisTTCTotal, setAutreFraisTTCTotal] = useState<number[]>([]);
  const [moyenneDelaisOK, setMoyenneDelaisOK] = useState<number[]>([]);
  const [moyenneJoursRemise, setMoyenneJoursRemise] = useState<number[]>([]);

  // Données du filtre
  const getDashoardFilterList = useCallback(() => {
    getDashoardFilter((!isAdmin && isGestionnaire) ? currentUser?.id?.toString() : '')
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setPartenaires(response?.data?.partenaires);
          setSources(response?.data?.sources);
          setStatuts(response?.data?.statuts);

          // Pré-selection de la source sur "SURPLUS AUTOS"
          setSourceQuery(response?.data?.sources
            ?.filter(source => source?.libelle === "SURPLUS AUTOS")
            ?.map((source) => source?.id)?.[0]?.toString());
          // Pré-selection de l'année sur l'année en cours
          setAnneeQuery((new Date())?.getFullYear()?.toString());
        }
      })
      .catch((error) => console.error(error))
      .finally(() => setIsFiltreLoading(false));
  }, [currentUser, isAdmin, isGestionnaire]);

  // Données des graphiques
  const getDashboardStatistics = useCallback(() => {
    setIsSearching(true);
    getStatisticIndicateur(
      (!isAdmin && isGestionnaire) ? currentUser?.id?.toString() : '',
      sourceQuery,
      anneeQuery,
      moisQuery,
      statutQuery,
      partenaireQuery
    )
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          // Tous les mois ou le mois selectionné dans le filtre
          setMonthData(prepareMonth(response.data?.statisticsLastYear));

          // Véhicule enlevés de l'année selectionné et de l'année avant
          setVehiculeEnleveTotal(prepareData(response.data?.statistics, 'totalVehiculeEnleve'));
          setVehiculeEnleveTotalLastYear(prepareData(response.data?.statisticsLastYear, 'totalVehiculeEnleve'));

          // Taux de service
          setMoyenneDelaisOK(prepareData(response.data?.statistics, 'moyenneDelaisOK'));

          // Frais avancés
          setRemorqueTTCTotal(prepareData(response.data?.statistics, 'totalRemorqueTTC'));
          setGardiennageTTCTotal(prepareData(response.data?.statistics, 'totalGardiennageTTC'));
          setAutreFraisTTCTotal(prepareData(response.data?.statistics, 'totalAutreFraisTTC'));

          // Délais
          setMoyenneJoursRemise(prepareData(response.data?.statistics, 'moyenneJoursRemise'));
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [currentUser, isAdmin, isGestionnaire, sourceQuery, anneeQuery, moisQuery, statutQuery, partenaireQuery]);

  // Méthode d'obtention des 5 dernières années
  const getYearData = () => {
    let result = [];
    for (let i = 0; i < 5; i++) {
      result.push(Number(new Date().getFullYear()) - i);
    }
    return result;
  };

  // useEffect pour définir le titre dans le header
  useEffect(() => {
    setTitle("Tableau de bord");
  }, [setTitle]);

  useEffect(() => {
    getDashoardFilterList();
  }, [getDashoardFilterList]);

  useEffect(() => {
    if (!isFiltreLoading) getDashboardStatistics();
  }, [isFiltreLoading, getDashboardStatistics]);

  // Méthode de formattage des données des selecteurs multiples
  const sourceData = sources?.map(source => {
    return {value: source?.id?.toString(), label: source?.libelle?.includes(' ') ? source?.libelle?.split(' ')?.[1] : source?.libelle}
  });
  const moisData = mois?.map(data => {
    return {value: data?.id?.toString(), label: data?.libelle}
  });
  const statutData = statuts?.map(statut => {
    return {value: statut?.id?.toString(), label: statut?.libelle}
  });
  const partenaireData = partenaires?.map(partenaire => {
    return {value: partenaire?.id.toString(), label: partenaire?.nom}
  });

  const formik = useFormik({
    initialValues: {
      source: (sourceData?.length > 0) ? sourceData.filter(source => source?.label?.includes('AUTOS')) : [],
      annee: (new Date())?.getFullYear()?.toString(),
      mois: [],
      statut: [],
      fournisseur: [],
      partenaire: [],
    },
    enableReinitialize: true,
    onSubmit: values => {
      setSourceQuery(values?.source?.length > 0 ? values?.source?.map((value) => value?.value)?.join(',') : '');
      setAnneeQuery(values?.annee);
      setMoisQuery(values?.mois?.length > 0 ? values?.mois?.map((value) => value?.value)?.join(',') : '');
      setStatutQuery(values?.statut?.length > 0 ? values?.statut?.map((value) => value?.value)?.join(',') : '');
      setPartenaireQuery(values?.partenaire?.length > 0 ? values?.partenaire?.map((value) => value?.value)?.join(',') : '');
    },
  });

  if (isFiltreLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen-fit animate-in fade-in duration-300 bg-gray-50">
      <div
        className="p-4 w-full min-h-[200px] flex items-end relative text-gray-800 bg-white shadow-md"
        style={{
          backgroundImage: `linear-gradient(to top right, white 25%, rgba(255,255,255,0) 100%), url('${PUBLIC_URL}/camion2.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: '50% 50%',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="w-1/3 p-4">
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            Bienvenue sur votre tableau d'indicateurs
          </h3>
          <p className="text-sm text-gray-500">
            Visualisez facilement votre activité avec nos différents sites.
          </p>
        </div>
      </div>

      <div>
        <div className="p-4 bg-gray-50 sticky top-0 z-10">
          <form onSubmit={formik.handleSubmit}>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="form-label">Source</label>
                <Select<Option | string, true>
                  name="source"
                  className="w-full form-input-select2"
                  classNamePrefix="form-input-select2"
                  placeholder="Tous"
                  isMulti
                  unstyled
                  options={sourceData}
                  onChange={option => formik.setFieldValue("source", option)}
                  value={formik.values.source ?? sourceData[1]}
                />
              </div>
              <div className="flex-1">
                <label className="form-label">Année</label>
                <select
                  name="annee"
                  className="w-full form-input form-select"
                  onChange={formik.handleChange}
                  value={formik.values.annee}
                >
                  {getYearData().map((year: number, key: number) => (
                    <option key={key} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="form-label">Mois</label>
                <Select<Option | string, true>
                  name="mois"
                  className="w-full form-input-select2"
                  classNamePrefix="form-input-select2"
                  placeholder="Tous"
                  isMulti
                  unstyled
                  options={moisData}
                  onChange={option => formik.setFieldValue("mois", option)}
                  value={formik.values.mois ?? moisData[0]}
                />
              </div>
              <div className="flex-1">
                <label className="form-label">Statut</label>
                <Select<Option | string, true>
                  name="statut"
                  className="w-full form-input-select2"
                  classNamePrefix="form-input-select2"
                  placeholder="Tous"
                  isMulti
                  unstyled
                  options={statutData}
                  onChange={option => formik.setFieldValue("statut", option)}
                  value={formik.values.statut ?? statutData[0]}
                />
              </div>
              <div className="flex-1">
                <label className="form-label">Partenaire</label>
                <Select<Option | string, true>
                  name="partenaire"
                  className="w-full form-input-select2"
                  classNamePrefix="form-input-select2"
                  placeholder="Tous"
                  isMulti
                  unstyled
                  options={partenaireData}
                  onChange={option => formik.setFieldValue("partenaire", option)}
                  value={formik.values.partenaire ?? partenaireData[0]}
                />
              </div>
              <div className="pt-[20px]">
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <IconFilter/>
                  Filtrer
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="px-4 pb-4 grid grid-cols-2 gap-4">
          <div className="p-4 bg-white shadow-md border border-gray-100 rounded-xl">
            <div className="text-center">
              <h3 className="font-bold text-gray-900">Nombre de véhicules enlevés</h3>
            </div>
            <div className="min-h-[330px] flex justify-center items-center">
              {isSearching ? (
                <IconSpinner className="animate-spin w-10 h-10"/>
              ) : (
                <BarChart
                  month={monthData}
                  total={[vehiculeEnleveTotalLastYear, vehiculeEnleveTotal]}
                  year={Number(anneeQuery || (new Date())?.getFullYear())}
                  height={330}
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="bg-white p-4 w-full shadow-md border border-gray-100 rounded-xl">
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900">Taux de service moyen</h3>
                </div>
                <p className="flex justify-center items-end gap-2 font-bold text-3xl min-h-[36px]">
                  {!isSearching ? (
                    <>
                      {moisQuery !== '' ? moyenneDelaisOK?.[0] : roundNumber((sumArrayData(moyenneDelaisOK) / sumArrayData(vehiculeEnleveTotal)) * 100)}
                      <span className="text-sm">%</span>
                    </>
                  ) : '...'}
                </p>
              </div>
            </div>
            <div className="bg-white p-4 shadow-md border border-gray-100 rounded-xl">
              <div className="text-center">
                <h3 className="font-bold text-gray-900">Taux de Service</h3>
                <h3 className="text-sm text-gray-500">Part des VH enlevés dans les délais</h3>
              </div>
              <div className="min-h-[200px] flex justify-center items-center">
                {isSearching ? (
                  <IconSpinner className="animate-spin w-10 h-10"/>
                ) : (
                  <LineChart
                    month={monthData}
                    total={moyenneDelaisOK}
                    totalVH={vehiculeEnleveTotal}
                    step={10}
                    height={200}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="p-4 bg-white shadow-md border border-gray-100 rounded-xl">
            <div className="text-center">
              <h3 className="font-bold text-gray-900">Frais avancés</h3>
            </div>
            <div className="min-h-[310px] flex justify-center items-center">
              {isSearching ? (
                <IconSpinner className="animate-spin w-10 h-10"/>
              ) : (
                <MultipleBarChart
                  month={monthData}
                  total={[remorqueTTCTotal, gardiennageTTCTotal, autreFraisTTCTotal]}
                  height={310}
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="bg-white p-4 w-full shadow-md border border-gray-100 rounded-xl">
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900">Délai moyen</h3>
                </div>
                <p className="flex justify-center items-end gap-2 font-bold text-3xl min-h-[36px]">
                  {!isSearching ? (moisQuery !== '' ? `${moyenneJoursRemise?.[0]} j` : `${calculateMoyenne(moyenneJoursRemise)} j`) : '...'}
                </p>
              </div>
            </div>
            <div className="bg-white p-4 shadow-md border border-gray-100 rounded-xl">
              <div className="text-center">
                <h3 className="font-bold text-gray-900">Délais en jours calendaires</h3>
              </div>
              <div className="min-h-[200px] flex justify-center items-center">
                {isSearching ? (
                  <IconSpinner className="animate-spin w-10 h-10"/>
                ) : (
                  <LineChart
                    month={monthData}
                    total={moyenneJoursRemise}
                    step={0.5}
                    isPercent={false}
                    height={200}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndicateurDashboardPage;